import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { COOKIE_CONSENT_EVENT, acceptCookies, shouldShowCookieBanner } from '@/lib/cookieConsent';

/**
 * Banner global de cookies.
 *
 * La **decisión** de mostrarlo vive en `@/lib/cookieConsent` y se verifica sin
 * React; acá queda sólo la presentación. Se monta una vez en el `AppShell`,
 * junto al banner de membresía, así que aparece en cualquier ruta.
 *
 * ⚠️ **Arranca oculto y se decide en `useEffect`**, no durante el render. Leer
 * el almacenamiento mientras se renderiza haría que el banner **parpadee** en
 * quien ya aceptó: se pinta y se va. Y si algún día esto se renderiza del lado
 * del servidor, ahí no hay `window` que leer.
 *
 * El enlace lleva a `/privacidad`, que es **pública y no pide sesión**: se lee
 * **antes** de aceptar, que es justo cuando sirve. Hasta que esa página existió,
 * el banner iba sin enlace a propósito — mandar a un 404 a quien quiere
 * informarse es peor que no ofrecerlo.
 *
 * 🔴 **`leftOffset` no es cosmético: sin él el banner TAPA la barra lateral.**
 * Medido en el navegador: a ancho completo, el banner (`z-50`) se superpone a
 * los botones "Ingresar" y "Crear cuenta" que viven al pie de la barra
 * (`z-30`), y **quedan imposibles de tocar** hasta que alguien acepte las
 * cookies. Es decir: el aviso legal bloqueaba justo la puerta de entrada.
 * Corriendo el borde izquierdo el ancho de la barra, el banner ocupa sólo el
 * contenido y los botones quedan libres.
 */
interface CookieBannerProps {
  /** Ancho de la barra lateral en px. `0` en móvil, donde la barra es superior. */
  leftOffset?: number;
}

export function CookieBanner({ leftOffset = 0 }: CookieBannerProps): JSX.Element | null {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(shouldShowCookieBanner());

    // Quien revoca desde el pie no es pariente de este componente, así que no
    // hay props ni contexto que los conecte: se enteran por un aviso global.
    // Sin esto, el pie borraría la decisión y el banner no reaparecería hasta
    // recargar — y quien tocó "preferencias" no vería pasar nada.
    const alCambiar = (): void => setVisible(shouldShowCookieBanner());
    window.addEventListener(COOKIE_CONSENT_EVENT, alCambiar);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, alCambiar);
  }, []);

  if (!visible) return null;

  function handleAccept(): void {
    // Se cierra pase lo que pase con el guardado: el visitante ya respondió, y
    // dejarle el banner puesto sería no escucharlo. Si no se pudo persistir
    // —almacenamiento bloqueado— volverá en la próxima visita, que es el
    // comportamiento correcto: sin registro, no consta consentimiento.
    acceptCookies();
    setVisible(false);
  }

  return (
    <div
      role="region"
      aria-label="Aviso de cookies"
      style={{ left: leftOffset }}
      className="fixed bottom-0 right-0 z-50 border-t border-fc-line bg-fc-paper/95 backdrop-blur transition-[left] duration-200"
    >
      <div className="container-fc flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-fc-muted">
          Usamos cookies para que la compra funcione y para entender cómo se usa el sitio.{' '}
          <Link to="/privacidad" className="underline hover:text-fc-ink">
            Conocé más
          </Link>
          .
        </p>
        <button
          type="button"
          onClick={handleAccept}
          className="shrink-0 rounded-card bg-fc-ink px-5 py-2 text-sm font-medium text-fc-paper transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-fc-gold/40"
        >
          Aceptar cookies
        </button>
      </div>
    </div>
  );
}
